import { createSlice } from '@reduxjs/toolkit';
import type {
  RejectedAsnItem,
  PartyAppEnrollment,
} from '../../services/api/packAnalysisService';
import {
  fetchRejectedAsns,
  reprocessAsn,
  reprocessAsnBatch,
  fetchEnrollments,
  createEnrollment,
  updateEnrollment,
  disableEnrollment,
} from '../thunks/packAnalysisThunks';

export interface PackAnalysisState {
  // Rejected ASN list
  rejectedAsns: RejectedAsnItem[];
  rejectedTotalCount: number;
  rejectedPageIndex: number;
  rejectedPageSize: number;
  isRejectedLoading: boolean;
  rejectedError: string | null;

  // Party-App Enrollments
  enrollments: PartyAppEnrollment[];
  enrollmentsTotalCount: number;
  enrollmentsPageIndex: number;
  enrollmentsPageSize: number;
  isEnrollmentsLoading: boolean;
  enrollmentsError: string | null;

  // Shared mutation state (create / update / disable / reprocess)
  isMutating: boolean;
  mutationError: string | null;
}

const initialState: PackAnalysisState = {
  rejectedAsns: [],
  rejectedTotalCount: 0,
  rejectedPageIndex: 1,
  rejectedPageSize: 20,
  isRejectedLoading: false,
  rejectedError: null,

  enrollments: [],
  enrollmentsTotalCount: 0,
  enrollmentsPageIndex: 1,
  enrollmentsPageSize: 20,
  isEnrollmentsLoading: false,
  enrollmentsError: null,

  isMutating: false,
  mutationError: null,
};

const packAnalysisSlice = createSlice({
  name: 'packAnalysis',
  initialState,
  reducers: {
    clearRejectedError: (state) => {
      state.rejectedError = null;
    },
    clearEnrollmentsError: (state) => {
      state.enrollmentsError = null;
    },
    clearMutationError: (state) => {
      state.mutationError = null;
    },
  },
  extraReducers: (builder) => {
    // --- Rejected ASN list ---
    builder
      .addCase(fetchRejectedAsns.pending, (state) => {
        state.isRejectedLoading = true;
        state.rejectedError = null;
      })
      .addCase(fetchRejectedAsns.fulfilled, (state, action) => {
        state.isRejectedLoading = false;
        state.rejectedAsns = action.payload.items;
        state.rejectedTotalCount = action.payload.totalCount;
        state.rejectedPageIndex = action.payload.pageIndex;
        state.rejectedPageSize = action.payload.pageSize;
      })
      .addCase(fetchRejectedAsns.rejected, (state, action) => {
        state.isRejectedLoading = false;
        state.rejectedError = action.payload as string;
      });

    // --- Single reprocess ---
    builder
      .addCase(reprocessAsn.pending, (state) => {
        state.isMutating = true;
        state.mutationError = null;
      })
      .addCase(reprocessAsn.fulfilled, (state, action) => {
        state.isMutating = false;
        const status = action.payload.response?.ingestStatus;
        // If the row moved off the rejected state, drop it from the local list.
        if (status && status !== 'rejected_not_enrolled') {
          state.rejectedAsns = state.rejectedAsns.filter(
            (r) => r.doc856Id !== action.payload.doc856Id
          );
          state.rejectedTotalCount = Math.max(0, state.rejectedTotalCount - 1);
        } else {
          // Still rejected — bump the local attempt counter so the UI reflects it.
          const row = state.rejectedAsns.find((r) => r.doc856Id === action.payload.doc856Id);
          if (row) row.reprocessAttempts = action.payload.response?.reprocessAttempts ?? row.reprocessAttempts;
        }
      })
      .addCase(reprocessAsn.rejected, (state, action) => {
        state.isMutating = false;
        state.mutationError = action.payload as string;
      });

    // --- Batch reprocess ---
    builder
      .addCase(reprocessAsnBatch.pending, (state) => {
        state.isMutating = true;
        state.mutationError = null;
      })
      .addCase(reprocessAsnBatch.fulfilled, (state) => {
        state.isMutating = false;
        // Page is refetched by the caller after a batch run.
      })
      .addCase(reprocessAsnBatch.rejected, (state, action) => {
        state.isMutating = false;
        state.mutationError = action.payload as string;
      });

    // --- Enrollments list ---
    builder
      .addCase(fetchEnrollments.pending, (state) => {
        state.isEnrollmentsLoading = true;
        state.enrollmentsError = null;
      })
      .addCase(fetchEnrollments.fulfilled, (state, action) => {
        state.isEnrollmentsLoading = false;
        state.enrollments = action.payload.items;
        state.enrollmentsTotalCount = action.payload.totalCount;
        state.enrollmentsPageIndex = action.payload.pageIndex;
        state.enrollmentsPageSize = action.payload.pageSize;
      })
      .addCase(fetchEnrollments.rejected, (state, action) => {
        state.isEnrollmentsLoading = false;
        state.enrollmentsError = action.payload as string;
      });

    // --- Enrollment mutations ---
    builder
      .addCase(createEnrollment.pending, (state) => { state.isMutating = true; state.mutationError = null; })
      .addCase(createEnrollment.fulfilled, (state) => { state.isMutating = false; })
      .addCase(createEnrollment.rejected, (state, action) => {
        state.isMutating = false;
        state.mutationError = action.payload as string;
      })
      .addCase(updateEnrollment.pending, (state) => { state.isMutating = true; state.mutationError = null; })
      .addCase(updateEnrollment.fulfilled, (state) => { state.isMutating = false; })
      .addCase(updateEnrollment.rejected, (state, action) => {
        state.isMutating = false;
        state.mutationError = action.payload as string;
      })
      .addCase(disableEnrollment.pending, (state) => { state.isMutating = true; state.mutationError = null; })
      .addCase(disableEnrollment.fulfilled, (state, action) => {
        state.isMutating = false;
        // Soft-delete — local list refetched by page; defensively mark inactive too.
        const row = state.enrollments.find((e) => e.enrollmentId === action.payload);
        if (row) row.isActive = false;
      })
      .addCase(disableEnrollment.rejected, (state, action) => {
        state.isMutating = false;
        state.mutationError = action.payload as string;
      });
  },
});

export const { clearRejectedError, clearEnrollmentsError, clearMutationError } = packAnalysisSlice.actions;
export default packAnalysisSlice.reducer;
